<template>
  <header class="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
    <div class="max-w-7xl mx-auto px-4">
      <nav class="flex h-14 items-center justify-between">
        <!-- Logo -->
        <router-link
          :to="{ name: 'home' }"
          class="flex items-center gap-2"
        >
          <img
            class="h-8 lg:h-10 w-auto"
            src="@/assets/images/logo.png"
            alt="UPChelas logotipo"
          />
          <span class="font-semibold text-lg text-foreground">UPChelas</span>
        </router-link>

        <!-- Mobile hamburger button -->
        <button
          @click="mobileMenuOpen = !mobileMenuOpen"
          class="lg:hidden h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-accent transition-colors duration-150"
          aria-label="Abrir menu"
        >
          <svg class="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              v-if="!mobileMenuOpen"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 6h16M4 12h16M4 18h16"
            />
            <path
              v-else
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <!-- Desktop navigation -->
        <div class="hidden lg:flex items-center gap-1">
          <router-link
            :to="{ name: 'home' }"
            class="nav-link"
            :class="{ 'nav-link-active': $route.name === 'home' }"
          >
            Início
          </router-link>

          <router-link
            :to="{ name: 'pastoralUnit' }"
            class="nav-link"
            :class="{ 'nav-link-active': $route.name === 'pastoralUnit' }"
          >
            Unidade Pastoral
          </router-link>

          <!-- Caridade dropdown -->
          <UDropdown
            ref="charityDropdown"
            :class="{ 'nav-link-active': charityRoutes.includes($route.name) }"
          >
            <template #trigger>
              <span class="nav-link-text" :class="{ 'font-medium': charityRoutes.includes($route.name) }">
                Caridade e Evangelização
              </span>
            </template>
            <router-link :to="{ name: 'caridade' }" class="dropdown-item">
              Grupo Paroquial Da Caridade
            </router-link>
            <router-link :to="{ name: 'saintBeaCenter' }" class="dropdown-item">
              Centro Social e Cultural de Santa Beatriz
            </router-link>
            <router-link :to="{ name: 'saintMaxCenter' }" class="dropdown-item">
              Centro Social Paroquial São Maximiliano de Kolbe
            </router-link>
            <router-link :to="{ name: 'missionarios' }" class="dropdown-item">
              Missionários
            </router-link>
            <router-link :to="{ name: 'voluntarios' }" class="dropdown-item">
              Voluntários
            </router-link>
          </UDropdown>

          <!-- Sacramentos dropdown -->
          <UDropdown
            ref="sacramentsDropdown"
            :class="{ 'nav-link-active': sacramentRoutes.includes($route.name) }"
          >
            <template #trigger>
              <span class="nav-link-text" :class="{ 'font-medium': sacramentRoutes.includes($route.name) }">
                Sacramentos
              </span>
            </template>
            <router-link :to="{ name: 'baptism' }" class="dropdown-item">
              Batismo
            </router-link>
            <router-link :to="{ name: 'marriage' }" class="dropdown-item">
              Matrimónio
            </router-link>
          </UDropdown>

          <!-- Vida Pastoral dropdown -->
          <UDropdown
            ref="pastoralDropdown"
            :class="{ 'nav-link-active': pastoralRoutes.includes($route.name) }"
          >
            <template #trigger>
              <span class="nav-link-text" :class="{ 'font-medium': pastoralRoutes.includes($route.name) }">
                Vida Pastoral
              </span>
            </template>
            <router-link :to="{ name: 'catechism' }" class="dropdown-item">
              Catequese
            </router-link>
            <router-link :to="{ name: 'ofs' }" class="dropdown-item">
              OFS
            </router-link>
            <router-link :to="{ name: 'scouts' }" class="dropdown-item">
              Escuteiros
            </router-link>
            <router-link :to="{ name: 'young' }" class="dropdown-item">
              Jovens
            </router-link>
            <router-link :to="{ name: 'family' }" class="dropdown-item">
              Família
            </router-link>
            <router-link :to="{ name: 'ecological' }" class="dropdown-item">
              Focus Ecológico
            </router-link>
          </UDropdown>

          <router-link
            :to="{ name: 'conchas' }"
            class="nav-link"
            :class="{ 'nav-link-active': $route.name === 'conchas' }"
          >
            Conchas
          </router-link>

          <router-link
            :to="{ name: 'contacts' }"
            class="nav-link"
            :class="{ 'nav-link-active': $route.name === 'contacts' }"
          >
            Contactos
          </router-link>
        </div>
      </nav>

      <!-- Mobile menu -->
      <Transition
        enter-active-class="transition ease-out duration-150"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-2"
      >
        <div v-show="mobileMenuOpen" class="lg:hidden py-4 border-t border-border">
          <div class="flex flex-col gap-1">
            <router-link
              :to="{ name: 'home' }"
              class="mobile-nav-link"
              :class="{ 'mobile-nav-link-active': $route.name === 'home' }"
              @click="mobileMenuOpen = false"
            >
              Início
            </router-link>

            <router-link
              :to="{ name: 'pastoralUnit' }"
              class="mobile-nav-link"
              :class="{ 'mobile-nav-link-active': $route.name === 'pastoralUnit' }"
              @click="mobileMenuOpen = false"
            >
              Unidade Pastoral
            </router-link>

            <!-- Caridade mobile -->
            <div class="py-1">
              <button
                @click="charityOpen = !charityOpen"
                class="w-full flex items-center justify-between px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors duration-150"
              >
                <span :class="{ 'font-medium text-foreground': charityRoutes.includes($route.name) }">
                  Caridade e Evangelização
                </span>
                <svg
                  class="w-4 h-4 transition-transform duration-150"
                  :class="{ 'rotate-180': charityOpen }"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div v-show="charityOpen" class="pl-4 mt-1 flex flex-col gap-0.5">
                <router-link :to="{ name: 'caridade' }" class="mobile-sub-link" @click="mobileMenuOpen = false">
                  Grupo Paroquial Da Caridade
                </router-link>
                <router-link :to="{ name: 'saintBeaCenter' }" class="mobile-sub-link" @click="mobileMenuOpen = false">
                  Centro Social e Cultural de Santa Beatriz
                </router-link>
                <router-link :to="{ name: 'saintMaxCenter' }" class="mobile-sub-link" @click="mobileMenuOpen = false">
                  Centro Social Paroquial São Maximiliano de Kolbe
                </router-link>
                <router-link :to="{ name: 'missionarios' }" class="mobile-sub-link" @click="mobileMenuOpen = false">
                  Missionários
                </router-link>
                <router-link :to="{ name: 'voluntarios' }" class="mobile-sub-link" @click="mobileMenuOpen = false">
                  Voluntários
                </router-link>
              </div>
            </div>

            <!-- Sacramentos mobile -->
            <div class="py-1">
              <button
                @click="sacramentsOpen = !sacramentsOpen"
                class="w-full flex items-center justify-between px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors duration-150"
              >
                <span :class="{ 'font-medium text-foreground': sacramentRoutes.includes($route.name) }">
                  Sacramentos
                </span>
                <svg
                  class="w-4 h-4 transition-transform duration-150"
                  :class="{ 'rotate-180': sacramentsOpen }"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div v-show="sacramentsOpen" class="pl-4 mt-1 flex flex-col gap-0.5">
                <router-link :to="{ name: 'baptism' }" class="mobile-sub-link" @click="mobileMenuOpen = false">
                  Batismo
                </router-link>
                <router-link :to="{ name: 'marriage' }" class="mobile-sub-link" @click="mobileMenuOpen = false">
                  Matrimónio
                </router-link>
              </div>
            </div>

            <!-- Vida Pastoral mobile -->
            <div class="py-1">
              <button
                @click="pastoralOpen = !pastoralOpen"
                class="w-full flex items-center justify-between px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors duration-150"
              >
                <span :class="{ 'font-medium text-foreground': pastoralRoutes.includes($route.name) }">
                  Vida Pastoral
                </span>
                <svg
                  class="w-4 h-4 transition-transform duration-150"
                  :class="{ 'rotate-180': pastoralOpen }"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div v-show="pastoralOpen" class="pl-4 mt-1 flex flex-col gap-0.5">
                <router-link :to="{ name: 'catechism' }" class="mobile-sub-link" @click="mobileMenuOpen = false">
                  Catequese
                </router-link>
                <router-link :to="{ name: 'ofs' }" class="mobile-sub-link" @click="mobileMenuOpen = false">
                  OFS
                </router-link>
                <router-link :to="{ name: 'scouts' }" class="mobile-sub-link" @click="mobileMenuOpen = false">
                  Escuteiros
                </router-link>
                <router-link :to="{ name: 'young' }" class="mobile-sub-link" @click="mobileMenuOpen = false">
                  Jovens
                </router-link>
                <router-link :to="{ name: 'family' }" class="mobile-sub-link" @click="mobileMenuOpen = false">
                  Família
                </router-link>
                <router-link :to="{ name: 'ecological' }" class="mobile-sub-link" @click="mobileMenuOpen = false">
                  Focus Ecológico
                </router-link>
              </div>
            </div>

            <router-link
              :to="{ name: 'conchas' }"
              class="mobile-nav-link"
              :class="{ 'mobile-nav-link-active': $route.name === 'conchas' }"
              @click="mobileMenuOpen = false"
            >
              Conchas
            </router-link>

            <router-link
              :to="{ name: 'contacts' }"
              class="mobile-nav-link"
              :class="{ 'mobile-nav-link-active': $route.name === 'contacts' }"
              @click="mobileMenuOpen = false"
            >
              Contactos
            </router-link>
          </div>
        </div>
      </Transition>
    </div>
  </header>
</template>

<script setup>
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import UDropdown from '@/components/ui/UDropdown.vue'

const route = useRoute()

const mobileMenuOpen = ref(false)
const charityOpen = ref(false)
const sacramentsOpen = ref(false)
const pastoralOpen = ref(false)

const charityDropdown = ref(null)
const sacramentsDropdown = ref(null)
const pastoralDropdown = ref(null)

const charityRoutes = ['caridade', 'saintBeaCenter', 'saintMaxCenter', 'missionarios', 'voluntarios']
const sacramentRoutes = ['baptism', 'marriage']
const pastoralRoutes = ['catechism', 'ecological', 'ofs', 'pilgrims', 'scouts', 'young', 'family']

// Close all dropdowns on route change
watch(() => route.path, () => {
  mobileMenuOpen.value = false
  charityOpen.value = false
  sacramentsOpen.value = false
  pastoralOpen.value = false
  charityDropdown.value?.close()
  sacramentsDropdown.value?.close()
  pastoralDropdown.value?.close()
})
</script>

<style scoped>
@reference "../css/tailwind.css";

.nav-link {
  @apply px-3 py-2 text-sm text-muted-foreground rounded-md hover:bg-accent hover:text-accent-foreground transition-colors duration-150;
}

.nav-link-text {
  @apply text-sm text-muted-foreground;
}

.nav-link-active {
  @apply bg-accent text-accent-foreground font-medium rounded-md;
}

.nav-link-active .nav-link-text {
  @apply text-accent-foreground;
}

.dropdown-item {
  @apply block rounded-sm px-2 py-1.5 text-sm text-foreground hover:bg-accent transition-colors duration-150;
}

.mobile-nav-link {
  @apply px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors duration-150;
}

.mobile-nav-link-active {
  @apply font-medium bg-accent text-accent-foreground;
}

.mobile-sub-link {
  @apply px-3 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors duration-150;
}
</style>
